/**
 * Converts pasted bullet text into JSON nodes for APPLY_CATEGORY_OUTLINE.
 *
 * Each non-empty line: leading spaces/tabs set nesting depth (2 space units or 4 columns per tab ≈ one level).
 * Optional list prefix is then stripped: numbered like `1.` / `12)` / `a)` (letter + closing paren), or a run of
 * common bullet symbols (`- * • › ▸` etc.) with optional spaces after each. Lines with only indentation and a title
 * (no bullet) are valid.
 *
 * Unified import: folder lines plus optional `Subcase:` headers and row lines under a folder (see {@link parseUnifiedTreeImport}).
 */

import {
  BULK_CREATE_CONTENT_MAX_ROWS,
  type BulkContentLineJson,
  parseBulkContentSubcaseBlocks,
} from './bulkContentPasteParse';

export type OutlineContentLineJson = { subcase: string; text: string };

export type OutlineNodeJson = {
  name: string;
  description?: string;
  children?: OutlineNodeJson[];
  /** Content rows for a leaf folder (no subfolders). Applied with the outline in one step. */
  lines?: OutlineContentLineJson[];
};

export type UnifiedImportParseResult =
  | { kind: 'tree'; nodes: OutlineNodeJson[]; error: null }
  | { kind: 'contentOnly'; lines: BulkContentLineJson[]; error: null }
  | { kind: 'error'; error: string };

const INDENT_UNITS_PER_LEVEL = 2;

/** Ordered list markers: digits or letters, then `.` or `)`, then space. */
const ORDERED_LIST_PREFIX = /^(?:(?:\d{1,4}|[a-zA-Z]{1,4})[.)]\s+)/;

/**
 * Visual bullets / markers (single codepoints or short ASCII), each optionally followed by spaces.
 * Intentionally excludes bare `.` and ellipsis so titles like `3.14` or `… notes` are not mangled.
 */
const LOOSE_BULLET_PREFIX =
  /^(?:[*+\-–—•‣›▸►▪▫□◻◇○>\[\]()\u2022-\u2025\u2043\u2219\u25AA-\u25AE\u25CB\u25CC\u25CF\u25E6→←↓↑◆◇★☆✓✗|\u2610\u2611]\s*)+/u;

function stripOptionalBulletPrefix(rest: string): string {
  const ordered = ORDERED_LIST_PREFIX.exec(rest);
  if (ordered) {
    return rest.slice(ordered[0].length);
  }
  const loose = LOOSE_BULLET_PREFIX.exec(rest);
  if (!loose) {
    return rest;
  }
  const after = rest.slice(loose[0].length);
  if (after.trim().length === 0) {
    return rest;
  }
  return after;
}

function measureIndentUnits(prefix: string): number {
  let n = 0;
  for (const ch of prefix) {
    if (ch === '\t') {
      n += 4;
    } else if (ch === ' ') {
      n += 1;
    }
  }
  return n;
}

function splitNameAndDescription(title: string): { name: string; description?: string } {
  const t = title.trim();
  const em = /\s+—\s+/.exec(t);
  if (em && em.index !== undefined && em.index > 0) {
    return {
      name: t.slice(0, em.index).trim(),
      description: t.slice(em.index + em[0].length).trim() || undefined,
    };
  }
  return { name: t };
}

/**
 * @returns roots array for `nodes` field (may be empty if nothing parseable).
 */
export function parseBulletOutlineToNodes(text: string): OutlineNodeJson[] {
  const rawLines = text.split(/\r?\n/).map((l) => l.replace(/\s+$/, ''));
  const rows: { indentUnits: number; title: string }[] = [];

  for (const line of rawLines) {
    if (!line.trim()) {
      continue;
    }
    const m = /^([\t ]*)(.*)$/.exec(line);
    if (!m) {
      continue;
    }
    const indentUnits = measureIndentUnits(m[1] ?? '');
    let rest = m[2] ?? '';
    rest = stripOptionalBulletPrefix(rest);
    const title = rest.trim();
    if (title) {
      rows.push({ indentUnits, title });
    }
  }

  if (rows.length === 0) {
    return [];
  }

  const minU = Math.min(...rows.map((r) => r.indentUnits));
  const normalized = rows.map((r) => ({
    depth: Math.max(0, Math.floor((r.indentUnits - minU) / INDENT_UNITS_PER_LEVEL)),
    title: r.title,
  }));

  const roots: OutlineNodeJson[] = [];
  const stack: { depth: number; node: OutlineNodeJson }[] = [];

  for (const { depth, title } of normalized) {
    const { name, description } = splitNameAndDescription(title);
    if (!name) {
      continue;
    }
    while (stack.length > 0 && stack[stack.length - 1].depth >= depth) {
      stack.pop();
    }
    const node: OutlineNodeJson = { name };
    if (description) {
      node.description = description;
    }
    if (stack.length === 0) {
      roots.push(node);
    } else {
      const parent = stack[stack.length - 1].node;
      if (!parent.children) {
        parent.children = [];
      }
      parent.children.push(node);
    }
    stack.push({ depth, node });
  }

  return roots;
}

/** Line is only "subcase:" (optional spaces after the colon). */
const SUBCASE_HEADER_LINE = /^(.+):\s*$/;

/**
 * Parse a combined paste: indented folder tree with optional content blocks under leaves, or content-only blocks.
 */
export function parseUnifiedTreeImport(text: string): UnifiedImportParseResult {
  const trimmed = text.trim();
  if (!trimmed) {
    return { kind: 'error', error: 'Paste an outline, content blocks, or both.' };
  }

  const rawLines = text.split(/\r?\n/);
  const physical: { indentUnits: number; body: string; lineNo: number }[] = [];

  for (let i = 0; i < rawLines.length; i++) {
    const line = rawLines[i].replace(/\s+$/, '');
    if (!line.trim()) {
      continue;
    }
    const m = /^([\t ]*)(.*)$/.exec(line);
    if (!m) {
      continue;
    }
    const indentUnits = measureIndentUnits(m[1] ?? '');
    let rest = m[2] ?? '';
    rest = stripOptionalBulletPrefix(rest);
    const body = rest.trim();
    if (body) {
      physical.push({ indentUnits, body, lineNo: i + 1 });
    }
  }

  if (physical.length === 0) {
    return { kind: 'error', error: 'No non-empty lines to parse.' };
  }

  if (SUBCASE_HEADER_LINE.test(physical[0].body)) {
    const bulk = parseBulkContentSubcaseBlocks(text);
    if (bulk.error) {
      return { kind: 'error', error: bulk.error };
    }
    return { kind: 'contentOnly', lines: bulk.lines, error: null };
  }

  const minU = Math.min(...physical.map((p) => p.indentUnits));
  const depthOf = (indentUnits: number) =>
    Math.max(0, Math.floor((indentUnits - minU) / INDENT_UNITS_PER_LEVEL));

  const roots: OutlineNodeJson[] = [];
  const stack: { depth: number; node: OutlineNodeJson }[] = [];
  let contentParent: OutlineNodeJson | null = null;
  let currentSubcase: string | null = null;
  let contentDepth: number | null = null;

  const closeContentBlock = () => {
    contentParent = null;
    currentSubcase = null;
    contentDepth = null;
  };

  for (const { indentUnits, body, lineNo } of physical) {
    const depth = depthOf(indentUnits);
    const header = SUBCASE_HEADER_LINE.exec(body);

    if (header) {
      const subcase = header[1].trim();
      if (!subcase) {
        return { kind: 'error', error: `Line ${lineNo}: empty subcase before ":".` };
      }
      while (stack.length > 0 && stack[stack.length - 1].depth >= depth) {
        stack.pop();
      }
      const parentEntry = stack.find((s) => s.depth === depth - 1);
      if (!parentEntry) {
        return {
          kind: 'error',
          error: `Line ${lineNo}: subcase "${subcase}" must sit under an indented folder line above it.`,
        };
      }
      if (parentEntry.node.children?.length) {
        return {
          kind: 'error',
          error: `Line ${lineNo}: folder "${parentEntry.node.name}" already has subfolders — content rows cannot share a folder with subfolders.`,
        };
      }
      contentParent = parentEntry.node;
      if (!contentParent.lines) {
        contentParent.lines = [];
      }
      currentSubcase = subcase;
      contentDepth = depth;
      continue;
    }

    if (currentSubcase !== null && contentParent !== null && contentDepth !== null) {
      if (depth >= contentDepth) {
        contentParent.lines!.push({ subcase: currentSubcase, text: body });
        continue;
      }
      closeContentBlock();
    }

    while (stack.length > 0 && stack[stack.length - 1].depth >= depth) {
      stack.pop();
    }
    const { name, description } = splitNameAndDescription(body);
    if (!name) {
      continue;
    }
    if (SUBCASE_HEADER_LINE.test(name)) {
      return {
        kind: 'error',
        error: `Line ${lineNo}: looks like a subcase header — use indentation under a folder name, or paste content-only blocks.`,
      };
    }

    const node: OutlineNodeJson = { name };
    if (description) {
      node.description = description;
    }

    if (stack.length === 0) {
      roots.push(node);
    } else {
      const parent = stack[stack.length - 1].node;
      if (parent.lines?.length) {
        return {
          kind: 'error',
          error: `Line ${lineNo}: cannot add subfolder "${name}" under "${parent.name}" — that folder already has content rows.`,
        };
      }
      if (!parent.children) {
        parent.children = [];
      }
      parent.children.push(node);
    }
    stack.push({ depth, node });
    closeContentBlock();
  }

  if (roots.length === 0) {
    return { kind: 'error', error: 'No folders parsed. Add folder lines or use "Subcase:" blocks for content-only paste.' };
  }

  const rowErr = validateOutlineContentRowLimits(roots);
  if (rowErr) {
    return { kind: 'error', error: rowErr };
  }

  return { kind: 'tree', nodes: roots, error: null };
}

function countOutlineContentRows(nodes: OutlineNodeJson[]): number {
  let n = 0;
  const walk = (items: OutlineNodeJson[]) => {
    for (const node of items) {
      if (node.lines?.length) {
        n += node.lines.length;
      }
      if (node.children?.length) {
        walk(node.children);
      }
    }
  };
  walk(nodes);
  return n;
}

function validateOutlineContentRowLimits(nodes: OutlineNodeJson[]): string | null {
  const total = countOutlineContentRows(nodes);
  if (total > BULK_CREATE_CONTENT_MAX_ROWS) {
    return `At most ${BULK_CREATE_CONTENT_MAX_ROWS} content rows in one import (got ${total}).`;
  }
  return null;
}

function validateOutlineNoChildrenAndLinesTogether(nodes: OutlineNodeJson[]): string | null {
  const walk = (items: OutlineNodeJson[]): string | null => {
    for (const n of items) {
      const hasLines = (n.lines?.length ?? 0) > 0;
      const hasChildren = (n.children?.length ?? 0) > 0;
      if (hasLines && hasChildren) {
        return `Folder "${n.name}" cannot have both subfolders and content rows.`;
      }
      if (hasChildren && n.children) {
        const inner = walk(n.children);
        if (inner) {
          return inner;
        }
      }
    }
    return null;
  };
  return walk(nodes);
}

/**
 * Server rule: any node with a non-empty {@code children} array must have at least two children.
 *
 * @returns an error message if invalid, otherwise {@code null}.
 */
export function validateOutlineMinTwoChildrenPerBranch(nodes: OutlineNodeJson[]): string | null {
  const walk = (items: OutlineNodeJson[]): string | null => {
    for (const n of items) {
      if (n.lines?.length) {
        continue;
      }
      const ch = n.children;
      if (ch !== undefined && ch.length === 1) {
        return `Each folder with subfolders must have at least two (only one under "${n.name}"). Merge levels or add a sibling.`;
      }
      if (ch !== undefined && ch.length > 1) {
        const inner = walk(ch);
        if (inner) {
          return inner;
        }
      }
    }
    return null;
  };
  return walk(nodes);
}

function validateOutlineDuplicateSiblingNamesAmong(items: OutlineNodeJson[], context: string): string | null {
  const seen = new Set<string>();
  for (const n of items) {
    const label = n.name.trim();
    const key = label.toLowerCase();
    if (!key) continue;
    if (seen.has(key)) {
      return `Two folders ${context} cannot share the same name "${label}". Rename one of them.`;
    }
    seen.add(key);
  }
  return null;
}

/** Direct child cannot reuse the parent's name (case-insensitive). */
export function validateOutlineNoChildNameEqualsParent(nodes: OutlineNodeJson[]): string | null {
  const walk = (n: OutlineNodeJson, parentName: string | null): string | null => {
    const name = n.name.trim();
    if (parentName !== null && name.toLowerCase() === parentName.toLowerCase()) {
      return `A child folder cannot use the same name as its parent ("${name}"). Rename one or the other.`;
    }
    const ch = n.children;
    if (!ch?.length) return null;
    for (const c of ch) {
      const inner = walk(c, name);
      if (inner) return inner;
    }
    return null;
  };
  for (const r of nodes) {
    const err = walk(r, null);
    if (err) return err;
  }
  return null;
}

/** Top-level outline folder cannot reuse the anchor category name (e.g. document root). */
export function validateOutlineNoTopLevelNameEqualsAnchor(
  nodes: OutlineNodeJson[],
  anchorCategoryName: string | null | undefined,
): string | null {
  const anchor = anchorCategoryName?.trim();
  if (!anchor) return null;
  const anchorKey = anchor.toLowerCase();
  for (const n of nodes) {
    const name = n.name.trim();
    if (name && name.toLowerCase() === anchorKey) {
      return `A top-level folder cannot use the same name as the category you are attaching to ("${name}"). Rename it or the parent category.`;
    }
  }
  return null;
}

/** Same-level folders must have distinct names (case-insensitive), including multiple roots under the anchor. */
export function validateOutlineNoDuplicateSiblingNames(nodes: OutlineNodeJson[]): string | null {
  const walk = (n: OutlineNodeJson): string | null => {
    const ch = n.children;
    if (ch && ch.length >= 2) {
      const dup = validateOutlineDuplicateSiblingNamesAmong(ch, `under "${n.name.trim()}"`);
      if (dup) return dup;
    }
    if (!ch?.length) return null;
    for (const c of ch) {
      const inner = walk(c);
      if (inner) return inner;
    }
    return null;
  };
  const dupTop = validateOutlineDuplicateSiblingNamesAmong(nodes, 'at the top level of the outline');
  if (dupTop) return dupTop;
  for (const r of nodes) {
    const err = walk(r);
    if (err) return err;
  }
  return null;
}

/**
 * Full client-side checks before sending APPLY_CATEGORY_OUTLINE (matches server rules).
 * Order: min two children per branch → duplicate sibling names → child name ≠ parent name → top-level ≠ anchor.
 */
export function validateOutlineForProposal(
  nodes: OutlineNodeJson[],
  anchorCategoryName?: string | null,
): string | null {
  return (
    validateOutlineNoChildrenAndLinesTogether(nodes) ??
    validateOutlineContentRowLimits(nodes) ??
    validateOutlineMinTwoChildrenPerBranch(nodes) ??
    validateOutlineNoDuplicateSiblingNames(nodes) ??
    validateOutlineNoChildNameEqualsParent(nodes) ??
    validateOutlineNoTopLevelNameEqualsAnchor(nodes, anchorCategoryName) ??
    null
  );
}

/**
 * New documents already have a root category (named like the document). When the outline is a single top-level
 * folder with at least two subfolders, attach those subfolders directly under the document root instead of creating
 * an extra wrapper that mirrors that shell.
 */
export type NewDocumentImportNaming =
  | {
      ok: true;
      documentName: string;
      outlineNodes: OutlineNodeJson[];
      /** True when the outline has no root element (multiple top-level lines) and the user supplied the document name. */
      explicitDocumentName: boolean;
    }
  | { ok: false; error: string };

/**
 * Resolves CREATE_DOCUMENT naming from parsed outline roots. One top-level line is the outline root and names the
 * document; multiple top-level lines mean no root — the user must supply {@link manualDocumentName}.
 */
export function resolveNewDocumentImportNaming(
  nodes: OutlineNodeJson[],
  manualDocumentName = '',
): NewDocumentImportNaming {
  if (nodes.length === 0) {
    return { ok: false, error: 'Add at least one folder line to the outline.' };
  }

  if (nodes.length === 1) {
    const documentName = nodes[0].name?.trim() ?? '';
    if (!documentName) {
      return {
        ok: false,
        error:
          'With a root line in the outline, that title becomes the new document name — add a non-empty root line.',
      };
    }
    return { ok: true, documentName, outlineNodes: nodes, explicitDocumentName: false };
  }

  const documentName = manualDocumentName.trim();
  if (!documentName) {
    return {
      ok: false,
      error:
        'Enter a name for the new document when the outline has no root element (multiple top-level lines).',
    };
  }
  return { ok: true, documentName, outlineNodes: nodes, explicitDocumentName: true };
}

export function hoistOutlineRootsForNewDocument(nodes: OutlineNodeJson[]): OutlineNodeJson[] {
  if (nodes.length !== 1) return nodes;
  const ch = nodes[0].children;
  if (!ch || ch.length === 0) return [];
  if (ch.length < 2) return nodes;
  return ch;
}

export function outlineHasNestedFolders(nodes: OutlineNodeJson[]): boolean {
  return nodes.some((n) => (n.children?.length ?? 0) > 0);
}

/**
 * When true, proposal uses CREATE_DOCUMENT (name only) + APPLY_CATEGORY_OUTLINE NEW_ROOT
 * instead of embedding nested folders on CREATE_DOCUMENT (server allows one level only there).
 */
export function shouldUseApplyOutlineOnNewDocument(
  nodes: OutlineNodeJson[],
  explicitDocumentName: boolean,
): boolean {
  if (explicitDocumentName) return true;
  if (outlineHasNestedFolders(nodes)) return true;
  if (outlineHasNestedFolders(hoistOutlineRootsForNewDocument(nodes))) return true;
  return false;
}

/**
 * Applying an outline under a **content** category (a row bucket, not the document root) requires at least two
 * top-level folders so the parent can become a branch.
 */
export function validateOutlineMinTopLevelForContentBucketAnchor(
  nodes: OutlineNodeJson[],
  anchorIsContentBucket: boolean,
): string | null {
  if (!anchorIsContentBucket || nodes.length >= 2) return null;
  return 'The selected category is a content bucket. Add at least two top-level folders in the outline so it can become a branch.';
}
